import { set, sumBy } from 'lodash';
import { PAYMENT_TYPE } from 'modules/POS/Cashier/cashier.constants';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import { ICashierCashPayment, ICashierCheckPayment, ICashierCreditCardPayment, ICashierGiftCardPayment, ICashierLoyaltyPointPayment, ICashierOtherPayment, ICashierPayment } from 'modules/POS/services/types/payment';
import shopService from 'modules/shop/services';
import { ICashIncentiveResData } from 'modules/shop/types/incentive';
import { parseFee } from 'utils/formatCurrency';
import { parseDecimal } from 'utils/number';
import { MULTI_PAYMENT_TYPES, PAYMENT_FORM_NAMES } from './constants';

export interface IFormMultiPaymentValues {
  detail: IBillInfoResData;
  ePayType: string;
  enableCardFee: boolean;
  ignorePrint: boolean;

  enableIncentive: boolean;
  cashIncentiveValue: number;
  checkIncentiveValue: number;
  incentivePercent: number;

  chance: number;
  otherCheckInfo: ICashierCheckPayment['checkInfo'];
  giftCardId: string;
  loyaltyPoint: number;
  tip: number;
  cardFee: number;
  cardFeeLabel: { percent: number, amount: number };

  availablePoint: number;
  availablePointExchange: number;
  redeemPoint: number;
  balancePoint: number;
  balancePointExchange: number;

  CREDIT_CARD: number;
  CASH: number;
  CHECK: number;
  VENMO: number;
  CASH_APPS: number;
  PAYPAL: number;
  ZELLE: number;
  GIFT_CARD: number;
  LOYALTY_POINT: number;
  GIFT_CREDIT: number;
  giftCreditCode: string;

  isValid_CREDIT_CARD: number;
  isValid_CASH: number;
  isValid_CHECK: number;
  isValid_VENMO: number;
  isValid_CASH_APPS: number;
  isValid_PAYPAL: number;
  isValid_ZELLE: number;
  isValid_GIFT_CARD: number;
  isValid_GIFT_CREDIT: number;

}

export const useCashIncentiveDiscount = (discountSetting: ICashIncentiveResData | null | undefined) => {
  const getCashIncentiveDiscount = (subTotal: number, enableDiscountIncentive?: boolean) => {
    if (!enableDiscountIncentive) return 0;
    if (!subTotal) return 0;
    if (!discountSetting) return 0;
    const { discountType, discountValue } = discountSetting;
    if (discountType === 'PERCENT') {
      const amount = subTotal * (discountValue / 100);
      return Math.round(amount);

    }
    if (discountType === 'MONEY') {
      const amount = discountValue;
      return Math.round(amount);
    }
    return 0;
  };

  const getAmountCashDiscount = (values: IFormMultiPaymentValues) => {
    const cash = values.CASH || 0;
    const check = values.CHECK || 0;
    const enable = values.enableIncentive || false;
    const total = values.detail?.total || 0;

    const amount = (cash || 0) + (check || 0);

    const finalAmount = amount > total ? total : amount;

    const amountDiscount = getCashIncentiveDiscount(finalAmount, enable);
    return amountDiscount;
  };
  const calcAmountDiscount = (amount: number, values: IFormMultiPaymentValues) => {
    const enable = values.enableIncentive || false;
    const total = values.detail?.total || 0;
    if (!amount) return 0;
    const finalAmount = amount > total ? total : amount;
    const amountDiscount = getCashIncentiveDiscount(finalAmount, enable);
    return amountDiscount;
  };

  const getLabelCashDiscount = (amount: number) => {
    if (!discountSetting) return ({ percent: 0, amount: 0 });
    const { discountType, discountValue } = discountSetting;
    if (discountType === 'PERCENT') {
      return ({
        percent: discountValue,
        amount: amount || 0,
      });
    }
    if (discountType === 'MONEY')
      return ({
        percent: 0,
        amount: discountValue,
      });
  };
  const getIncentivePercent = () => {
    if (!discountSetting) return 0;
    const { discountType, discountValue } = discountSetting;
    if (discountType === 'PERCENT') {
      return discountValue;
    }
    if (discountType === 'MONEY')
      return 0;
  };

  return ({
    getAmountCashDiscount,
    getLabelCashDiscount,
    calcAmountDiscount,
    getIncentivePercent,
  });
};

export const useCardFee = () => {
  const feeCreditCard = shopService.selectors.feeCreditCard();

  const getAmountCardFee = (values: IFormMultiPaymentValues) => {
    if (!values.enableCardFee) return 0;
    if (!values.CREDIT_CARD) return 0;
    if (!feeCreditCard) return 0;
    if (feeCreditCard?.feeType === 'PERCENT')
      return parseFee(values.CREDIT_CARD * (feeCreditCard?.feeValue / 100));
    if (feeCreditCard?.feeType === 'MONEY')
      return feeCreditCard?.feeValue || 0;
    return 0;
  };

  const getLabelCardFee = (amount: number) => {
    if (!feeCreditCard) return ({ percent: 0, amount: 0 });
    if (feeCreditCard?.feeType === 'PERCENT')
      return ({
        percent: feeCreditCard?.feeValue,
        amount: amount || 0,
      });
    if (feeCreditCard?.feeType === 'MONEY')
      return ({
        percent: 0,
        amount: feeCreditCard?.feeValue || 0,
      });
  };
  return ({ getAmountCardFee, getLabelCardFee });
};

export const calcCashChance = (values: IFormMultiPaymentValues, discount?: number) => {
  const cardFee = values.cardFee || 0;
  const remaining = sumBy([
    values.detail?.total || 0,
    values.CREDIT_CARD ? cardFee : 0,
  ]) - sumBy([
    values.CREDIT_CARD ? values.CREDIT_CARD + cardFee : values.CREDIT_CARD,
    values.CHECK, values.ZELLE,
    values.PAYPAL, values.CASH_APPS, values.VENMO,
    values.GIFT_CARD, values.LOYALTY_POINT, values.GIFT_CREDIT,
  ], o => o || 0) - (discount || 0);

  const _remaining = remaining < 0 ? 0 : remaining;
  const chance = values.CASH - _remaining;
  return chance > 0 ? chance : 0;
};

export const calcRemaining = (values: IFormMultiPaymentValues) => {
  const total = values.detail?.total || 0;
  const cardFee = values?.cardFee || 0;
  const creditCardValue = values.CREDIT_CARD || 0;
  const cashValue = values.CASH || 0;
  const checkValue = values.CHECK || 0;
  const remaining = parseDecimal(sumBy([
    total,
    creditCardValue ? cardFee : 0,
    cashValue ? - values.cashIncentiveValue : 0,
    checkValue ? - values.checkIncentiveValue : 0,
  ])) - parseDecimal(sumBy([
    creditCardValue ? creditCardValue + cardFee : creditCardValue,
    cashValue - values.cashIncentiveValue,
    checkValue - values.checkIncentiveValue,
    values.ZELLE || 0, values.PAYPAL || 0,
    values.CASH_APPS || 0, values.VENMO || 0,
    values.GIFT_CARD || 0, values.LOYALTY_POINT || 0,
  ], o => o || 0));

  return remaining < 0 ? 0 : remaining;
};

export const calcMaxPayment = (values: IFormMultiPaymentValues, type: MULTI_PAYMENT_TYPES) => {
  const otherValueFields = PAYMENT_FORM_NAMES.filter(o => o !== type);
  const arrs: { key: string, value: any }[] = [];
  Object.entries(values).map(([key, value]) => {
    if (otherValueFields.includes(key as MULTI_PAYMENT_TYPES)) {
      arrs.push({ key, value: value || 0 });
    }
  });
  const total = values.detail?.total || 0;
  const paymentTotal = +sumBy(arrs, o => o.value).toFixed(2);
  const result = total - paymentTotal;
  return result > 0 ? result : 0;
};

export const getMultiplePaymentInfo = (values: IFormMultiPaymentValues) => {
  const paymentInfo: ICashierPayment[] = [];
  if (values.CASH) {
    const finalAmount = (values.CASH ?? 0) - (values.cashIncentiveValue || 0);
    const result: ICashierCashPayment = {
      paymentType: PAYMENT_TYPE.CASH,
      chance: Math.abs(values.chance || 0),
      amount: finalAmount,
    };
    if (values.enableIncentive) {
      set(result, 'cashIncentiveDiscount', true);
      set(result, 'discountIncentive', values.cashIncentiveValue);
    }
    paymentInfo.push(result);
  }
  if (values.CHECK) {
    const finalAmount = (values.CHECK ?? 0) - (values.checkIncentiveValue || 0);
    const result: ICashierCheckPayment = {
      paymentType: PAYMENT_TYPE.CHECK,
      amount: finalAmount,
      checkInfo: values.otherCheckInfo,
    };
    if (values.enableIncentive) {
      set(result, 'cashIncentiveDiscount', true);
      set(result, 'discountIncentive', values.checkIncentiveValue);
    }
    paymentInfo.push(result);
  }
  if (values.VENMO) {
    const result: ICashierOtherPayment = {
      paymentType: PAYMENT_TYPE.VENMO,
      amount: values.VENMO ?? 0,
      otherMethod: PAYMENT_TYPE.VENMO,
    };
    paymentInfo.push(result);
  }
  if (values.CASH_APPS) {
    const result: ICashierOtherPayment = {
      paymentType: PAYMENT_TYPE.CASH_APPS,
      amount: values.CASH_APPS ?? 0,
      otherMethod: PAYMENT_TYPE.CASH_APPS,
    };
    paymentInfo.push(result);
  }
  if (values.ZELLE) {
    const result: ICashierOtherPayment = {
      paymentType: PAYMENT_TYPE.ZELLE,
      amount: values.ZELLE ?? 0,
      otherMethod: PAYMENT_TYPE.ZELLE,
    };
    paymentInfo.push(result);
  }
  if (values.PAYPAL) {
    const result: ICashierOtherPayment = {
      paymentType: PAYMENT_TYPE.PAYPAL,
      amount: values.PAYPAL ?? 0,
      otherMethod: PAYMENT_TYPE.PAYPAL,
    };
    paymentInfo.push(result);
  }
  if (values.GIFT_CARD) {
    const result: ICashierGiftCardPayment = {
      paymentType: PAYMENT_TYPE.GIFT_CARD,
      amount: values.GIFT_CARD ?? 0,
      giftCardId: values.giftCardId,
    };
    paymentInfo.push(result);
  }
  if (values.LOYALTY_POINT) {
    const result: ICashierLoyaltyPointPayment = {
      paymentType: PAYMENT_TYPE.LOYALTY_POINT,
      amount: values.LOYALTY_POINT ?? 0,
      point: values.loyaltyPoint,
    };
    paymentInfo.push(result);
  }
  if (values.CREDIT_CARD) {
    const finalAmount = (values.CREDIT_CARD ?? 0) + (values.enableCardFee ? values.cardFee : 0);
    const result: ICashierCreditCardPayment = {
      paymentType: PAYMENT_TYPE.CREDIT_CARD,
      amount: finalAmount,
      chargeFee: !!values.enableCardFee,
      feeCredit: values.enableCardFee ? values.cardFee : 0,
    };
    paymentInfo.push(result);
  }

  return paymentInfo;
};

export const usePointExchangeDollar = () => {
  const loyaltySetting = shopService.selectors.loyaltyRedeem();
  const exchangeDollar = (point: number) => {
    if (!loyaltySetting) return 0;

    return +(point) / loyaltySetting?.oneDollarToPoint;
  };

  const exchangePoint = (_amount: number) => {
    if (!loyaltySetting) return 0;
    return (+(_amount || 0)) * (loyaltySetting?.oneDollarToPoint || 0);
  };

  return ({
    exchangeDollar,
    exchangePoint,
  });
};
